import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Loader2, Send, Eye, MessageCircle, TrendingUp, Activity, Sparkles } from "lucide-react";
import { API, buildHeaders } from "./shared";

const TIME_AGO = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
};

function KPI({ label, value, sub, accent, testid }) {
  return (
    <div className="rounded-xl border bg-white p-4" style={{ borderColor: "#e5e7eb" }} data-testid={testid}>
      <div className="text-[10.5px] font-bold tracking-[0.14em] uppercase text-[#94a3b8]">{label}</div>
      <div className="mt-1.5 text-[28px] font-bold tracking-[-0.02em] leading-none" style={{ color: accent }}>{value}</div>
      {sub && <div className="mt-1.5 text-[11.5px] text-[#64748b]">{sub}</div>}
    </div>
  );
}

function Bar({ label, sent, opened, replied, max }) {
  const pctSent = max ? (sent / max) * 100 : 0;
  const pctOpened = max ? (opened / max) * 100 : 0;
  const pctReplied = max ? (replied / max) * 100 : 0;
  return (
    <div className="space-y-1" data-testid={`results-bar-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between text-[12px]">
        <span className="font-semibold text-[#0f172a]">{label}</span>
        <span className="text-[#64748b]">{sent} sent · {opened} opened · {replied} replied</span>
      </div>
      <div className="relative h-2.5 rounded-full overflow-hidden" style={{ background: "#f1f5f9" }}>
        <div className="absolute inset-y-0 left-0" style={{ width: `${pctSent}%`, background: "#3B4FE4" }} />
        <div className="absolute inset-y-0 left-0" style={{ width: `${pctOpened}%`, background: "#C79A3B" }} />
        <div className="absolute inset-y-0 left-0" style={{ width: `${pctReplied}%`, background: "#16a34a" }} />
      </div>
    </div>
  );
}

export default function ResultsTab({ session, campaign }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => buildHeaders(session), [session]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/campaigns/${campaign.id}/results`, { headers });
      setData(r.data);
    } catch { toast.error("Failed to load results"); }
    finally { setLoading(false); }
  }, [campaign.id, headers]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-14" data-testid="results-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#94a3b8]" />
      </div>
    );
  }

  const t = data.totals || {};
  const maxBar = Math.max(1, ...(data.per_wave || []).map((p) => p.sent));

  return (
    <div data-testid="results-tab" className="space-y-5">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KPI label="Recipients" value={t.total_recipients || 0} sub={`${t.total_schools || 0} schools`} accent="#0f172a" testid="results-kpi-recipients" />
        <KPI label="Sent" value={t.sent || 0} sub="Mocked sends" accent="#3B4FE4" testid="results-kpi-sent" />
        <KPI label="Opened" value={t.opened || 0} sub={`${t.open_rate || 0}% open rate`} accent="#C79A3B" testid="results-kpi-opened" />
        <KPI label="Replied" value={t.replied || 0} sub={`${t.reply_rate || 0}% reply rate`} accent="#16a34a" testid="results-kpi-replied" />
        <KPI label="Status Mix" value={`${(data.status_counts?.replied || 0)}/${(data.status_counts?.queued || 0)}`} sub="Replied / Queued" accent="#0f172a" testid="results-kpi-status" />
      </div>

      {/* Per-wave breakdown */}
      <section className="rounded-2xl border bg-white p-5" style={{ borderColor: "#e5e7eb" }} data-testid="results-per-wave">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[14px] font-bold text-[#0f172a] flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> Per-Wave Breakdown
          </h3>
          <div className="text-[10.5px] flex items-center gap-2 text-[#94a3b8]">
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: "#3B4FE4" }} /> Sent</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: "#C79A3B" }} /> Opened</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: "#16a34a" }} /> Replied</span>
          </div>
        </div>
        <div className="space-y-3">
          {(data.per_wave || []).length === 0 ? (
            <div className="text-center py-6 text-[#94a3b8] text-[13px]">No waves yet.</div>
          ) : (
            (data.per_wave || []).map((p) => (
              <Bar key={p.wave_id} label={`Wave ${p.wave_number} — ${p.name}`} sent={p.sent} opened={p.opened} replied={p.replied} max={maxBar} />
            ))
          )}
        </div>
      </section>

      {/* Activity feed */}
      <section className="rounded-2xl border bg-white p-5" style={{ borderColor: "#e5e7eb" }} data-testid="results-activity">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[14px] font-bold text-[#0f172a] flex items-center gap-2">
            <Activity className="h-4 w-4" /> Recent Activity
          </h3>
          <span className="text-[11px] text-[#94a3b8]">{data.activity?.length || 0} events</span>
        </div>
        {(data.activity || []).length === 0 ? (
          <div className="text-center py-6 text-[#94a3b8] text-[13px]">No activity yet. Send a wave to seed activity.</div>
        ) : (
          <ul className="divide-y" style={{ borderColor: "#f1f5f9" }}>
            {data.activity.map((a) => (
              <li key={a.id} className="py-2.5 flex items-center gap-3" data-testid={`activity-row-${a.id}`}>
                <span
                  className="inline-flex items-center justify-center h-8 w-8 rounded-full shrink-0"
                  style={{ background: "rgba(59,79,228,0.12)", color: "#3B4FE4" }}
                >
                  <Send className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] text-[#0f172a]">
                    <strong>Wave {a.wave_number}</strong> sent to{" "}
                    <span className="font-semibold">{a.leader_name}</span> at <span>{a.school_name}</span>
                  </div>
                  <div className="text-[11px] text-[#94a3b8]">{TIME_AGO(a.sent_at)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="rounded-md px-3 py-2 text-[11.5px] flex items-center gap-1.5"
        style={{ background: "rgba(199,154,59,0.10)", color: "#92400e" }}>
        <Sparkles className="h-3 w-3" />
        Open/reply metrics in Phase 2 are derived from per-school status. Wire a real ESP webhook for true delivery, opens, and replies.
      </div>
    </div>
  );
}
